// Convert between the stored 0..1 fractional coordinates and on-screen pixel
// values for a given rendered image size. Used by every grid-rendering surface
// (operator, display, live, layout editor).

import type { ZoneDoc } from "../types";

export interface PixelBox {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
}

export interface PixelCircle {
  cx: number;
  cy: number;
  r: number;
  rotation: number;
}

/** Render-time conversion for a rectangle zone. */
export function zoneRectPx(zone: ZoneDoc, imgW: number, imgH: number): PixelBox {
  return {
    x: (zone.x ?? 0) * imgW,
    y: (zone.y ?? 0) * imgH,
    w: (zone.w ?? 0) * imgW,
    h: (zone.h ?? 0) * imgH,
    rotation: zone.rotation || 0,
  };
}

/** Render-time conversion for a circle zone. Radius scales by the smaller axis
 * to keep the shape circular under non-square aspect ratios. */
export function zoneCirclePx(zone: ZoneDoc, imgW: number, imgH: number): PixelCircle {
  const minAxis = Math.min(imgW, imgH);
  return {
    cx: (zone.cx ?? 0) * imgW,
    cy: (zone.cy ?? 0) * imgH,
    r: (zone.r ?? 0) * minAxis,
    rotation: zone.rotation || 0,
  };
}

export interface PixelQuad {
  // The four image-fraction corners scaled to pixel space, in TL/TR/BR/BL
  // order. Stored flat (x1, y1, x2, y2, x3, y3, x4, y4) so it drops straight
  // into both SVG `<polygon points="">` and Konva `<Line points={[]}>`.
  pts: number[];
  // Axis-aligned centroid (mean of the four corners) — used by all surfaces
  // for the label position, which stays upright per the design call.
  cx: number;
  cy: number;
}

/** Render-time conversion for a quad zone (perspective-projected cell). The
 * stored `corners` are 4 image-fraction points (TL, TR, BR, BL). Falls back
 * to a tiny box at the legacy x/y when `corners` is missing so a misshapen
 * doc still gets drawn somewhere visible instead of crashing the renderer. */
export function zoneQuadPx(zone: ZoneDoc, imgW: number, imgH: number): PixelQuad {
  const corners = zone.corners ?? [];
  if (corners.length !== 4) {
    const fx = (zone.x ?? 0.45) * imgW;
    const fy = (zone.y ?? 0.45) * imgH;
    const fw = (zone.w ?? 0.05) * imgW;
    const fh = (zone.h ?? 0.05) * imgH;
    return {
      pts: [fx, fy, fx + fw, fy, fx + fw, fy + fh, fx, fy + fh],
      cx: fx + fw / 2,
      cy: fy + fh / 2,
    };
  }
  const pts: number[] = [];
  let sx = 0, sy = 0;
  for (const c of corners) {
    const px = c.x * imgW, py = c.y * imgH;
    pts.push(px, py);
    sx += px; sy += py;
  }
  return { pts, cx: sx / 4, cy: sy / 4 };
}

/** Given a container size + the source image's aspect ratio, compute the
 * largest letterboxed image size that fits. Returns w/h plus the offset within
 * the container so child shapes can be positioned relative to the image edge. */
export function fitImageInContainer(
  containerW: number,
  containerH: number,
  aspectRatio: number  // width / height of the source image
): { imgW: number; imgH: number; offsetX: number; offsetY: number } {
  const containerAR = containerW / containerH;
  let imgW: number, imgH: number;
  if (aspectRatio > containerAR) {
    // Image is wider than container → constrained by width
    imgW = containerW;
    imgH = containerW / aspectRatio;
  } else {
    imgH = containerH;
    imgW = containerH * aspectRatio;
  }
  return {
    imgW,
    imgH,
    offsetX: (containerW - imgW) / 2,
    offsetY: (containerH - imgH) / 2,
  };
}
